import classNames from 'classnames';

import BottomSheetModal from '../BottomSheetModal/BottomSheetModal';
import { getPendingCtaLabel } from '../../utils/renewal';
import { formatRenewalErrorCode } from '../../utils/analytics/renewalStep';
import RenewalIllustration, { RenewalWordmark } from './RenewalIllustration';

import './RenewalSheet.scss';

/**
 * The dark renewal bottom sheet (Figma "Bottom sheet – …" frames). One layout for
 * every renewal message; the copy and the action come from utils/renewal.js.
 *
 * @param {object} props
 * @param {object} props.content - { illustration, showWordmark, title, body,
 *   summary, ctaLabel, secondaryLabel, dismissLabel, footnote, errorCode }; `summary`
 *   is a list of `{ label, value, emphasis }` rows (a price breakdown); `errorCode` an
 *   RN-xxx code shown under the body
 * @param {Function} props.onAction - primary button
 * @param {Function} props.onClose - close button, dismiss link, backdrop, Escape
 * @param {Function} [props.onSecondary] - the secondary button (`secondaryLabel`),
 *   an action of its own rather than a dismiss
 * @param {boolean} [props.isPending]
 * @param {boolean} [props.isCtaDisabled]
 * @param {string|null} [props.pendingLabel] - what the primary button says while
 *   pending, when not its action's own (e.g. the secondary action is the one running)
 * @return {JSX.Element}
 */
const RenewalSheet = ({
  content, onAction, onClose, onSecondary = null, isPending = false, isCtaDisabled = false,
  pendingLabel = null,
}) => {
  const {
    illustration, showWordmark, title, body, summary, ctaLabel, secondaryLabel, dismissLabel,
    footnote, errorCode,
  } = content;

  return (
    <BottomSheetModal
      isOpen
      onClose={onClose}
      title={title}
      hideCloseButton
      className={classNames('RenewalSheet', { 'RenewalSheet--art': !!illustration })}
    >
      <div className="RenewalSheet__header">
        <button
          type="button"
          className="RenewalSheet__close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <path d="M4.5 4.5l9 9M13.5 4.5l-9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {illustration && (
        <div className="RenewalSheet__art">
          {showWordmark && <RenewalWordmark className="RenewalSheet__wordmark" />}
          <RenewalIllustration illustration={illustration} slot="sheet" />
        </div>
      )}

      <div className="RenewalSheet__content">
        <div className={classNames('RenewalSheet__text', { 'RenewalSheet__text--centered': !!illustration })}>
          <h2 className="RenewalSheet__title">{title}</h2>
          <p className="RenewalSheet__body">{body}</p>
          {errorCode && <p className="RenewalSheet__errorCode">{formatRenewalErrorCode(errorCode)}</p>}
        </div>
        {summary?.length > 0 && (
          <dl className="RenewalSheet__summary">
            {summary.map(({ label, value, emphasis }) => (
              <div
                key={label}
                className={classNames('RenewalSheet__summaryRow', { 'RenewalSheet__summaryRow--emphasis': emphasis })}
              >
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        )}
        {/* The primary and secondary buttons read as one group: tighter than the
            sheet's own spacing (design review, 2026-10-20). */}
        <div className="RenewalSheet__ctaGroup">
          <button
            type="button"
            className="RenewalSheet__cta"
            onClick={onAction}
            disabled={isPending || isCtaDisabled}
            aria-busy={isPending}
          >
            {(isPending && (pendingLabel || getPendingCtaLabel(content.action))) || ctaLabel}
          </button>
          {secondaryLabel && onSecondary && (
            <button
              type="button"
              className="RenewalSheet__secondary"
              onClick={onSecondary}
              disabled={isPending}
            >
              {secondaryLabel}
            </button>
          )}
        </div>
        {footnote && <p className="RenewalSheet__footnote">{footnote}</p>}
        {dismissLabel && (
          <button type="button" className="RenewalSheet__dismiss" onClick={onClose}>
            {dismissLabel}
          </button>
        )}
      </div>
    </BottomSheetModal>
  );
};

export default RenewalSheet;
